import { useEffect, useRef, useCallback } from "react";
import { IShop } from "../interfaces/Shop";
import { IOrder } from "../interfaces/Order";
import { isEqual } from "lodash";

function getTemplateIframe(): HTMLIFrameElement | null {
  return document.getElementById(
    "storefront-template-iframe"
  ) as HTMLIFrameElement | null;
}

function sendIframeValue(frame: HTMLIFrameElement | null) {
  frame.contentWindow.postMessage({
    call: "sendShopValue",
    value: {
      hideIntro: true,
      isPreviewing: true,
    },
  });
}

function sendIframeFilterChange(
  filter: string,
  frame: HTMLIFrameElement | null
) {
  if (frame) {
    frame.contentWindow.postMessage({
      call: "sendShopFilterChange",
      value: {
        filter,
      },
    });
  }
}

export const useIframe = (entity: IShop | IOrder) => {
  const customizationRef = useRef<any>(null);
  const prevEntityRef = useRef<IShop | IOrder | null>(null);

  const handleIframeLoad = useCallback((ev: Event) => {
    const currentPage = customizationRef.current?.getCurrentPage();
    sendIframeValue(ev.target as HTMLIFrameElement);
    currentPage &&
      sendIframeFilterChange(currentPage, ev.target as HTMLIFrameElement);
  }, []);

  useEffect(() => {
    const elem = getTemplateIframe();
    if (elem) {
      elem.addEventListener("load", handleIframeLoad);

      if (
        elem.contentWindow &&
        elem.contentDocument?.readyState === "complete"
      ) {
        sendIframeValue(elem);
        const currentPage = customizationRef.current?.getCurrentPage();
        currentPage && sendIframeFilterChange(currentPage, elem);
      }

      if (
        prevEntityRef.current === null ||
        !isEqual(entity, prevEntityRef.current)
      ) {
        const entityType = entity.shop_id ? "shop" : "order";
        const entityId = entity.shop_id || entity.order_id;
        elem.src = `${window.location.origin}/${entityType}/${entityId}?hideHelp=true&previewShop=true`;
        prevEntityRef.current = entity;
      }
    }

    return () => {
      const elem = getTemplateIframe();
      if (elem) {
        elem.removeEventListener("load", handleIframeLoad);
      }
    };
  }, [handleIframeLoad, entity]);

  return { customizationRef, handleIframeLoad, sendIframeFilterChange };
};
